import React, {
  ForwardRefExoticComponent,
  RefAttributes,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  OrderRollingOfferEvents,
  RollingOfferProps,
  SubRollingOfferProps,
} from '../shared/types';
import {
  StyledHugeRollingOffer,
  StyledHugeRollingOfferContainer,
  StyledHugeRollingOfferWrapper,
} from './style';
import {
  arrowHeightWidth,
  containerPadding,
  maxHeight,
  rollingOfferUtils,
  subOfferHeight,
} from '../shared/utils';
import { AnimatePresence } from 'framer-motion';
import { HugeSubRollingOfferWrapper } from './components/SubWrapper';
import { ArrowWrapper } from './components/ArrowWrapper';

export const HugeRollingOffer: ForwardRefExoticComponent<
  RollingOfferProps & RefAttributes<OrderRollingOfferEvents>
> = React.forwardRef<OrderRollingOfferEvents, RollingOfferProps>(
  (
    {
      offerId,
      backgroundImage,
      backgroundColor = { colorOne: '#323281' },
      borderColor = { colorOne: '#FFCC4D', colorTwo: '#ffff' },
      borderWidth = 0,
      template,
      indexToCollect,
      collectBehavior,
      arrowColor = '#EEC519',
      subRollingOffersProducts,
      subRollingOfferUiProps,
      selectOffer,
      isPreview = false,
      timer,
    },
    ref,
  ) => {
    const elementRef = useRef<HTMLDivElement | null>(null);
    const [isAnimatingArrow, setIsAnimatingArrow] = useState(false);
    const [isAnimatingOffer, setIsAnimatingOffer] = useState(false);
    const [isAnimatingLayout, setIsAnimatingLayout] = useState(false);
    const [layoutSettings, setLayoutSettings] = useState({
      duration: 0,
    });
    const [subOffers, setSubOffers] = useState<SubRollingOfferProps[]>([]);
    const [subOfferWidth, setSubOfferWidth] = useState(0);
    const [height, setHeight] = useState<number>(0);
    const [heightDifference, setHeightDifference] = useState<number>(0);

    useEffect(() => {
      if (isAnimatingOffer || !subOffers?.length) return;

      const newHeight = Math.min(
        Math.ceil(subOffers?.length / 2) * (subOfferHeight + arrowHeightWidth) -
          arrowHeightWidth +
          containerPadding * 2,
        maxHeight - 4,
      );

      setHeight(newHeight);
      setHeightDifference(height - newHeight);
    }, [subOffers?.length, isAnimatingOffer]);

    useEffect(() => {
      if (!isAnimatingLayout) return;

      const checkHeight = setInterval(() => {
        if (elementRef.current) {
          const actualHeight = elementRef.current.offsetHeight;
          if (actualHeight === height) {
            setIsAnimatingLayout(false);
            clearInterval(checkHeight);
          }
        }
      }, 50);

      return () => clearInterval(checkHeight);
    }, [isAnimatingLayout, height]);

    useEffect(() => {
      setSubOffers(
        rollingOfferUtils.getSubRollingOffersFromProducts({
          indexToCollect,
          subRollingOffersProducts,
          subRollingOfferUiProps,
          collectBehavior,
          selectOffer: () => {
            if (subOffers?.length !== 1 && !isPreview) {
              setLayoutSettings({
                duration: 0.6,
              });
            }

            selectOffer();
          },
        }),
      );
    }, [indexToCollect, subRollingOffersProducts, subRollingOfferUiProps]);

    useImperativeHandle(ref, () => ({
      onOrderSuccess,
      onOrderFail,
    }));

    const onOrderSuccess = () => {
      setIsAnimatingArrow(true);
      setIsAnimatingOffer(true);
    };

    const onOrderFail = () => {
      setIsAnimatingArrow(false);
      setIsAnimatingOffer(false);
    };

    useEffect(() => {
      const updateSubWidth = () => {
        if (elementRef.current) {
          const containerWidth =
            elementRef.current.offsetWidth -
            containerPadding * 2 -
            borderWidth * 2;

          setSubOfferWidth((containerWidth - arrowHeightWidth) / 2);
        }
      };

      updateSubWidth();

      window.addEventListener('resize', updateSubWidth);
      return () => {
        window.removeEventListener('resize', updateSubWidth);
      };
    }, []);

    return (
      <StyledHugeRollingOfferWrapper
        backgroundImage={backgroundImage}
        backgroundColor={backgroundColor}
        borderColor={borderColor}
        borderWidth={borderWidth}
        marginBottom={isAnimatingLayout ? heightDifference : 0}
      >
        {timer && <div className="timer">{`Ends in: ${timer}`}</div>}
        <StyledHugeRollingOfferContainer
          layout
          ref={elementRef}
          key={offerId}
          initial={{ opacity: 1, scale: 1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0 }}
          transition={{
            layout: layoutSettings,
            exit: { duration: 0.5 },
          }}
          style={{
            borderRadius: '15px',
            boxSizing: 'border-box',
          }}
          isPreview={isPreview}
          className={`rolling-offer-container ${template}`}
          onLayoutAnimationStart={() => {
            setIsAnimatingLayout(true);
            setTimeout(() => {
              setIsAnimatingLayout(false);
            }, layoutSettings.duration * 1000 + 100);
          }}
          onLayoutAnimationComplete={() => setIsAnimatingLayout(false)}
        >
          <StyledHugeRollingOffer
            className={'rolling-offer'}
            height={height}
            isScrollable={isPreview && subOffers?.length > 6}
          >
            {subOfferWidth && (
              <AnimatePresence onExitComplete={() => setIsAnimatingOffer(false)}>
                {subOffers.map((subOffer, index) => (
                  <HugeSubRollingOfferWrapper
                    key={subOffer.index}
                    index={index}
                    subOffer={subOffer}
                    isAnimating={
                      (isAnimatingArrow || isAnimatingOffer) && !isPreview
                    }
                    width={subOfferWidth}
                    isPreview={isPreview}
                  />
                ))}
              </AnimatePresence>
            )}
            <AnimatePresence onExitComplete={() => setIsAnimatingArrow(false)}>
              {Array.from({ length: subOffers.length - 1 }, (_, index) => (
                <ArrowWrapper index={index} key={index} arrowColor={arrowColor} />
              ))}
            </AnimatePresence>
          </StyledHugeRollingOffer>
        </StyledHugeRollingOfferContainer>
      </StyledHugeRollingOfferWrapper>
    );
  },
);
